import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  schema?: object | object[];
  speakable?: boolean;
}

// Lightweight SEO component — no react-helmet-async dependency
// Updates meta tags directly via DOM after paint, so it never blocks FCP
export const SEO = ({ title, description, image, url, schema, speakable = true }: SEOProps) => {
  const siteName = "Dra. Adriana Rodrigues";
  const fullTitle = title.includes(siteName) ? title : `${title} — ${siteName}`;
  const siteUrl = "https://webpreview.aquinoagency.com";
  const currentUrl = url ? `${siteUrl}${url}` : siteUrl;
  const ogImage = image || "https://vibe.filesafe.space/1779507543897153858/assets/6cd812ff-b67e-46fa-ac49-553e5bc2cb3b.png";

  useEffect(() => {
    // Title
    document.title = fullTitle;

    const setMeta = (selector: string, value: string) => {
      const el = document.querySelector<HTMLMetaElement>(selector);
      if (el) el.content = value;
    };

    const setLink = (selector: string, value: string) => {
      const el = document.querySelector<HTMLLinkElement>(selector);
      if (el) el.href = value;
    };

    setMeta('meta[name="description"]', description);
    setLink('link[rel="canonical"]', currentUrl);

    // OG
    setMeta('meta[property="og:title"]', fullTitle);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', currentUrl);
    setMeta('meta[property="og:image"]', ogImage);

    // Twitter
    setMeta('meta[name="twitter:title"]', fullTitle);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:url"]', currentUrl);
    setMeta('meta[name="twitter:image"]', ogImage);

    // Page-specific schema
    if (schema || speakable) {
      // Remove previously injected page schemas
      document.querySelectorAll('script[data-page-schema]').forEach(el => el.remove());

      if (speakable) {
        const speakableSchema = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": currentUrl,
          "name": fullTitle,
          "description": description,
          "inLanguage": "pt-BR",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "h2", ".speakable"]
          }
        };
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-page-schema', '1');
        s.textContent = JSON.stringify(speakableSchema);
        document.head.appendChild(s);
      }

      if (schema) {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-page-schema', '1');
        s.textContent = JSON.stringify(schema);
        document.head.appendChild(s);
      }
    }
  }, [fullTitle, description, currentUrl, ogImage]);

  return null;
};
